/**
 * Eco Office (Gold) — the client-side half, issue #363.
 *
 * The frontend holds **no waste data**. Unit weights, emission factors, unit
 * costs, the behaviour multipliers and the organisation's cost-per-kg all live
 * server-side and drive the calculation there; the client only knows how to
 * render whatever the schema hands it.
 *
 * An add-row assessment: the user adds a row per waste type they generate and
 * picks the type from a dropdown, the same shape as Procurement. Six types are
 * counted in units and E-waste is weighed in kilograms.
 */

/**
 * `enums.py` carries both `ECO_OFFICE = "Eco Office"` and, under a
 * "Gold tier categories" comment, an otherwise unreferenced
 * `ECO_OFFICES = "Eco Offices"`. The backend maps both to `ECO_OFFICE`, and
 * `tierConfig` says "Eco Offices", so match either.
 */
export const ECO_OFFICE_CATEGORIES = ["Eco Office", "Eco Offices"] as const;

export const ECO_OFFICE_CATEGORY = ECO_OFFICE_CATEGORIES[0];

export const isEcoOfficeCategory = (category: string | null): boolean =>
  category !== null &&
  (ECO_OFFICE_CATEGORIES as readonly string[]).includes(category);

/** Column labels exactly as the schema serves them. Matched by label because
 * the backend owns the ids and the client must not assume their values. */
export const ECO_OFFICE_COLUMNS = {
  wasteType: "Waste Type",
  monthlyQuantity: "Monthly Quantity",
  monthlyWeightKg: "Monthly E-waste Weight (kg)",
  binsAccessible: "Bins Accessible",
  signageClear: "Signage Clear",
  reusableAvailable: "Reusable Alternative Available",
  recyclingProgram: "Recycling Program Available",
} as const;

/** The one type measured by weight rather than counted. */
export const E_WASTE = "E-waste";

/**
 * Which amount column a row uses, given its waste type.
 *
 * The backend rejects a row carrying both (`reject_mixed_quantities`), but the
 * schema does not express which one applies, so the rule lives here as an
 * affordance. The server validates it independently, which is the only reason
 * a client-side rule is acceptable.
 *
 * An unpicked row returns null: neither amount field applies until a type is
 * chosen.
 */
export const amountColumnFor = (wasteType: string): string | null => {
  if (!wasteType || wasteType.trim() === "") return null;
  return wasteType === E_WASTE
    ? ECO_OFFICE_COLUMNS.monthlyWeightKg
    : ECO_OFFICE_COLUMNS.monthlyQuantity;
};

/** True when this column is the amount field the row does not use. */
export const isUnusedAmountColumn = (
  columnLabel: string,
  wasteType: string,
): boolean => {
  const amountLabels: string[] = [
    ECO_OFFICE_COLUMNS.monthlyQuantity,
    ECO_OFFICE_COLUMNS.monthlyWeightKg,
  ];
  if (!amountLabels.includes(columnLabel)) return false;
  const applicable = amountColumnFor(wasteType);
  return applicable !== null && applicable !== columnLabel;
};

/**
 * Behaviour questions this row has not answered yet, by column label.
 *
 * Mirrors `EcoOfficeRowInput.is_complete`. It has to exist because the schema
 * marks only Waste Type required, and that column became the row label when the
 * rows were seeded — so `requiredColumns` filters it out for being
 * non-editable and the generic check is left with nothing at all to look at.
 *
 * Which questions a row owes is taken from its own `non_editable_columns`
 * rather than restated here. The server already excludes bins and signage for
 * the single-use types and the reusable question for cartridges and e-waste,
 * so reading that back is both shorter and incapable of drifting from it.
 */
export const ecoOfficeMissingColumnsFor = (
  columns: readonly { id: number | string; label: string }[],
  values: Record<string, string>,
  nonEditableColumns: readonly (string | number)[] | null | undefined,
): string[] => {
  const skipped = new Set((nonEditableColumns ?? []).map(String));
  const answered = (label: string): boolean => {
    const column = columns.find((c) => c.label === label);
    if (!column) return true;
    const value = values[String(column.id)];
    return value !== undefined && value.trim() !== "";
  };
  const asked = (label: string): boolean => {
    const column = columns.find((c) => c.label === label);
    return Boolean(column) && !skipped.has(String(column?.id));
  };

  // Untouched rows are not started, the same rule the other catalogues use and
  // the same one `has_any_data` applies server-side.
  if (!Object.values(values).some((v) => v !== undefined && v.trim() !== "")) {
    return [];
  }

  const amountLabel = asked(ECO_OFFICE_COLUMNS.monthlyWeightKg)
    ? ECO_OFFICE_COLUMNS.monthlyWeightKg
    : ECO_OFFICE_COLUMNS.monthlyQuantity;
  if (!answered(amountLabel)) return [amountLabel];

  const amountColumn = columns.find((c) => c.label === amountLabel);
  const amount = Number(values[String(amountColumn?.id)]);
  if (!Number.isFinite(amount) || amount < 0) return [amountLabel];
  // Zero says the lab does not produce this waste, and the backend asks no more.
  if (amount === 0) return [];

  return [
    ECO_OFFICE_COLUMNS.recyclingProgram,
    ECO_OFFICE_COLUMNS.binsAccessible,
    ECO_OFFICE_COLUMNS.signageClear,
    ECO_OFFICE_COLUMNS.reusableAvailable,
  ].filter((label) => asked(label) && !answered(label));
};

/**
 * Compact header text for the wide table only.
 *
 * Assessment content is capped at ~1104px (`.l0-layout`), so a column can be
 * under 90px. Rather than shrink type below the legible floor or break words
 * mid-character, the header is shortened and the full wording is carried by the
 * tooltip and the aria-label. The stacked layout has room and keeps full labels.
 */
export const ECO_OFFICE_HEADER_LABELS: Record<string, string> = {
  [ECO_OFFICE_COLUMNS.wasteType]: "Waste Type",
  [ECO_OFFICE_COLUMNS.monthlyQuantity]: "Qty / month",
  [ECO_OFFICE_COLUMNS.monthlyWeightKg]: "kg / month",
  [ECO_OFFICE_COLUMNS.binsAccessible]: "Bins",
  [ECO_OFFICE_COLUMNS.signageClear]: "Signage",
  [ECO_OFFICE_COLUMNS.reusableAvailable]: "Reusable",
  [ECO_OFFICE_COLUMNS.recyclingProgram]: "Recycling",
};

/** The schema sends every column with a null description, so all help text is
 * written here. */
export const ECO_OFFICE_TOOLTIPS: Record<string, string> = {
  [ECO_OFFICE_COLUMNS.wasteType]:
    "The kind of office waste this row is about. Add a row for each one your team produces.",
  [ECO_OFFICE_COLUMNS.monthlyQuantity]:
    "How many of these your team uses or throws out in a typical month. Estimates are fine.",
  [ECO_OFFICE_COLUMNS.monthlyWeightKg]:
    "Roughly how many kilograms of electronic waste your team disposes of in a month. Only used for E-waste.",
  [ECO_OFFICE_COLUMNS.binsAccessible]:
    "Are the right bins for this waste easy to find and use where people work?",
  [ECO_OFFICE_COLUMNS.signageClear]:
    "Is there clear signage showing what can and cannot go in this bin?",
  [ECO_OFFICE_COLUMNS.reusableAvailable]:
    "Is a reusable alternative in place, such as reusable cups, containers or cutlery?",
  [ECO_OFFICE_COLUMNS.recyclingProgram]:
    "Is there an active recycling or take-back program for this waste type on site?",
};

/**
 * Twelfths. The seven columns plus Remove must total 12, or `table-fixed`
 * scales everything down until labels break mid-word.
 *
 * Waste Type carries the long option text ("Paper Reams (500 sheets)") and gets
 * the width; the four yes/no columns need room for "Select" and no more.
 */
export const ECO_OFFICE_COLUMN_WIDTH: Record<string, string> = {
  [ECO_OFFICE_COLUMNS.wasteType]: "w-3/12",
  [ECO_OFFICE_COLUMNS.monthlyQuantity]: "w-2/12",
  [ECO_OFFICE_COLUMNS.monthlyWeightKg]: "w-2/12",
  [ECO_OFFICE_COLUMNS.binsAccessible]: "w-1/12",
  [ECO_OFFICE_COLUMNS.signageClear]: "w-1/12",
  [ECO_OFFICE_COLUMNS.reusableAvailable]: "w-1/12",
  [ECO_OFFICE_COLUMNS.recyclingProgram]: "w-1/12",
};
